import { 
  PublicClientApplication, 
  AuthenticationResult, 
  AccountInfo,
  InteractionRequiredAuthError,
  PopupRequest
} from '@azure/msal-browser';
import { msalConfig, loginRequest, popupLoginRequest } from '../config/msalConfig';
import { logger } from './logger';

// Create the MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Get the active account
 * @returns The active account or null if no active account exists
 */
export const getActiveAccount = (): AccountInfo | null => {
  const activeAccount = msalInstance.getActiveAccount();
  
  if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
    // If there's no active account but there are accounts, set the first one as active
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    return msalInstance.getAllAccounts()[0];
  }
  
  return activeAccount;
};

/**
 * Handle the response from a popup or redirect operation
 * @param response The authentication result
 * @returns The authentication result
 */
export const handleResponse = (response: AuthenticationResult): AuthenticationResult => {
  if (response !== null) {
    msalInstance.setActiveAccount(response.account);
  }
  
  return response;
};

/**
 * Sign in with a popup
 * @returns A promise that resolves to the authentication result
 */
export const signInWithPopup = async (): Promise<AuthenticationResult> => {
  try {
    const response = await msalInstance.loginPopup(popupLoginRequest);
    return handleResponse(response);
  } catch (error) {
    logger.error('Error during MSAL popup login', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  const account = getActiveAccount();
  if (account) {
    await msalInstance.logoutPopup({
      account,
    });
  }
};

/**
 * Get access token for API calls
 * @param request The token request
 * @returns A promise that resolves to the authentication result
 */
export const getTokenSilent = async (request: PopupRequest): Promise<AuthenticationResult> => {
  const account = getActiveAccount();
  
  if (!account) {
    throw new Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
  }
  
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...request,
      account,
    });
    
    return response;
  } catch (error) {
    // If silent token acquisition fails, try interactive method
    if (error instanceof InteractionRequiredAuthError) {
      return msalInstance.acquireTokenPopup({
        ...request,
        account,
      });
    }
    
    throw error;
  }
};

/**
 * Get ID token claims
 * @returns The ID token claims or null if no active account
 */
export const getIdTokenClaims = () => {
  const account = getActiveAccount();
  
  if (!account) {
    return null;
  }
  
  return account.idTokenClaims;
};

/**
 * Extract user information from ID token claims
 * @returns User information object
 */
export const getUserInfoFromIdToken = () => {
  const claims = getIdTokenClaims();
  
  if (!claims) {
    return null;
  }
  
  return {
    email: claims.preferred_username || claims.email,
    name: claims.name,
    firstName: claims.given_name,
    lastName: claims.family_name,
  };
};

/**
 * Get access token for backend authentication
 * @returns A promise that resolves to the access token
 */
export const getAccessTokenForBackend = async (): Promise<string> => {
  try {
    const response = await getTokenSilent(loginRequest);
    return response.idToken;
  } catch (error) {
    logger.error('Error getting access token for backend', { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

export default msalInstance;
